import { useState } from 'react';

// ACT! 4.0-style Notes tab: type → Enter → the date/time stamps itself on the
// left and the entry is saved to the database IMMEDIATELY (no Save button).
// Entries are stored inside contact.notes as "[MM/DD/YYYY h:mm AM] text" lines,
// newest first — so they survive export/migration as plain text.

const ENTRY_RE = /^\[([0-9]{2}\/[0-9]{2}\/[0-9]{4}[^\]]*)\]\s?(.*)$/;

const parseNotes = (raw) => {
  // Timestamped lines become grid entries; everything else is preserved in
  // the "imported" legacy row (regardless of position — new notes prepend,
  // so imported text usually trails at the bottom).
  const entries = [];
  const legacy = [];
  for (const line of (raw || '').split('\n')) {
    const m = line.match(ENTRY_RE);
    if (m) entries.push({ date: m[1], text: m[2] });
    else if (line.trim()) legacy.push(line);
  }
  return { entries, legacy: legacy.join('\n').trim() };
};

const stamp = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  let h = d.getHours();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()} ${h}:${min} ${ap}`;
};

export const ActNotesTab = ({ notes, onChange, onCommit }) => {
  const [draft, setDraft] = useState('');
  const [rawMode, setRawMode] = useState(false);
  const [saved, setSaved] = useState(false);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    const updated = `[${stamp()}] ${text}` + (notes && notes.trim() ? `\n${notes}` : '');
    setDraft('');
    await onCommit(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const { entries, legacy } = parseNotes(notes);

  return (
    <div className="p-3" data-testid="tab-content-notes">
      <div className="flex gap-2 mb-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
          placeholder="Type a note and press Enter — date & time stamp automatically"
          className="flex-1 px-2 py-1.5 text-[12px] bg-white border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]"
          data-testid="note-quick-input"
        />
        <button
          onClick={addNote}
          className="px-3 py-1.5 text-[11px] font-bold bg-[#0B5394] text-white hover:bg-[#094276]"
          data-testid="note-add-btn"
        >
          Add Note
        </button>
      </div>

      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] ${saved ? 'text-[#2D5F1A] font-bold' : 'text-[#777]'}`} data-testid="note-save-status">
          {saved ? '✓ Note saved' : `${entries.length} note${entries.length === 1 ? '' : 's'}`}
        </span>
        <button
          onClick={() => setRawMode(!rawMode)}
          className="text-[10px] text-[#0B5394] hover:underline"
          data-testid="note-raw-toggle"
        >
          {rawMode ? 'Back to note list' : 'Edit / delete notes'}
        </button>
      </div>

      {rawMode ? (
        <textarea
          value={notes}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
          className="w-full h-[220px] p-2 text-[12px] bg-white border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394] resize-none"
          placeholder="Enter notes here..."
          data-testid="card-notes"
        />
      ) : (
        <div className="border border-[#B0B0B0] bg-white max-h-[300px] overflow-y-auto" data-testid="notes-grid">
          <div className="grid grid-cols-[150px_1fr] bg-[#E0E0E0] border-b border-[#CCC] text-[10px] font-bold text-[#333] uppercase tracking-wide sticky top-0">
            <div className="px-2 py-1 border-r border-[#CCC]">Date</div>
            <div className="px-2 py-1">Note</div>
          </div>
          {entries.length === 0 && !legacy && (
            <div className="px-2 py-6 text-center text-[11px] text-[#999]" data-testid="notes-empty">
              No notes yet — type above and press Enter.
            </div>
          )}
          {entries.map((en, i) => (
            <div key={i} className="grid grid-cols-[150px_1fr] border-b border-[#EEE] text-[12px]">
              <div className="px-2 py-1.5 border-r border-[#EEE] text-[#555] whitespace-nowrap" data-testid={`note-date-${i}`}>{en.date}</div>
              <div className="px-2 py-1.5 whitespace-pre-wrap" data-testid={`note-text-${i}`}>{en.text.trim()}</div>
            </div>
          ))}
          {legacy && (
            <div className="grid grid-cols-[150px_1fr] border-b border-[#EEE] text-[12px]">
              <div className="px-2 py-1.5 border-r border-[#EEE] text-[#999] italic">imported</div>
              <div className="px-2 py-1.5 whitespace-pre-wrap text-[#333]" data-testid="note-legacy">{legacy}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
